import { useState, useEffect, useContext, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MovementTaskCompletionModal from '../../components/MovementTaskCompletionModal/MovementTaskCompletionModal';
import { useYardData } from '../YardManagement/yardDataFetch';
import { getAuthToken } from '../../utils/cookieUtils';
import { UserContext } from '../../App';
import { canCancelTasks } from '../../utils/roleHelper';
import { useCampus } from 'hooks/useCampus';
import CampusFacilityMultiSelect from 'components/Campus/CampusFacilityMultiSelect';
import { campusOptions, facilityDisplayName, facilityShortLabel, selectedCampusUuid } from 'utils/campusLabels';
import {
  Paper,
  Typography,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  Tooltip,
  TextField,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  Refresh,
  AssignmentInd,
  AssignmentReturn,
  CheckCircle,
  Cancel,
  DirectionsCar,
  Close,
  Edit,
  Search,
  Clear,
  Add,
  Remove,
  DragIndicator,
  Pause,
  PlayArrow,
  SwapHoriz
} from '@mui/icons-material';
import {
  saveFacilityToStorage,
  loadFacilityFromStorage,
  formatFacilityOptions
} from '../../utils/facilityStorage';
import { fetchArchivedUserTasks } from './api';
import { FacilityFilterChoice, resolveFacilityFilter, taskMatchesFacilityFilter } from './facilityFilter';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Task filter values
const FILTER_ACTIVE = 'active' as const;
const FILTER_ARCHIVED = 'archived' as const;
type TaskFilter = typeof FILTER_ACTIVE | typeof FILTER_ARCHIVED;

// Interface for facility
interface Facility {
  id: number;
  name: string;
  facility_alias?: string | null;
  configuration?: string | object;
  campus?: { uuid: string; name: string } | null;
}

// Types for tasks
interface TaskTemplate {
  uuid: string;
  name: string;
  task_template_json: any;
  account_id?: string;
}

interface TaskType {
  uuid: string;
  type: string;
  class: string;
}

interface TaskStatus {
  uuid: string;
  status: string;
}

// Union types for location objects
interface DockObject {
  uuid: string;
  name: string;
  status?: string;
}

interface YardSpotObject {
  uuid: string;
  name: string;
  facilityZoneUuid?: string;
}

interface TrailerObject {
  uuid: string;
  number: string;
}

interface ExternalLocationObject {
  name: string;
}

interface FacilityZoneObject {
  uuid: string;
  name: string;
}

interface Trailer {
  trailer_number: string;
  carrier_name?: string;
  lineage_uuid?: string;
  lineage_notes?: string;
}

interface Task {
  uuid: string;
  data: any;
  due_at: string | null;
  completed_at: string | null;
  user_id: string | null;
  facility_id: string;
  /** Where the task is filed: for a movement, the facility the trailer is going to. */
  facility?: { id: string; name: string; facility_alias?: string | null; configuration?: any } | null;
  source_facility_id?: string | null;
  cross_facility?: boolean | null;
  /** Destination is only a zone and the facility requires a specific spot or dock to complete. */
  requires_destination_selection?: boolean | null;
  driver_id: string | null;
  completed_by_user_id: string | null;
  completed_by_driver_id: string | null;
  modified_by_user_id: string | null;
  metadata: any;
  is_paused: boolean;
  created_at: string;
  updated_at: string;
  task_template: TaskTemplate | null;
  task_type: TaskType | null;
  task_status: TaskStatus | null;
  from_location: string | null;
  to_location: string | null;
  from_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
  to_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
  trailer: Trailer | null;
  related_movement_task: {
    uuid: string;
    from_location: string | null;
    to_location: string | null;
    from_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
    to_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
    data: {
      movement_type: string;
    };
  } | null;
}



const TaskApp = () => {
  const theme = useTheme();
  // @ts-ignore
  const { user } = useContext(UserContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for facilities
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };

  // The task app shows the whole campus of the selected facility at once.
  const { campus, campusFacilities, isMultiFacility } = useCampus(selectedFacility);
  // Only users spanning several campuses pick one; facilities within it are narrowed by the facility filter.
  const campusSelectOptions = campusOptions(facilities);
  const selectedCampus = selectedCampusUuid(facilities, selectedFacility?.id);
  const campusFacilityName = (facilityId?: string | null) =>
    facilityShortLabel(campusFacilities.find(f => String(f.id) === String(facilityId)));

  // Available tasks in a multi-facility campus: which facilities to show (see ./facilityFilter).
  const [facilityFilterChoice, setFacilityFilterChoice] = useState<FacilityFilterChoice | null>(null);
  const facilityFilter = resolveFacilityFilter(campusFacilities, facilities.map(f => f.id), facilityFilterChoice);
  const setFacilityFilterIds = (ids: string[]) =>
    setFacilityFilterChoice({ campusKey: facilityFilter.campusKey, ids });
  
  // State
  const [tasks, setTasks] = useState<Task[]>([]);
  // State to track ALL tasks in facility for group completion validation
  const [allFacilityTasks, setAllFacilityTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [processingTasks, setProcessingTasks] = useState<Set<string>>(new Set());

  // Task detail modal state
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = useState<boolean>(false);
  
  // Movement task completion modal state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<Task | null>(null);
  
  // Get facility data for the modal
  const {
    facilities: modalFacilities,
    facilityZones: modalFacilityZones,
    yardSpots: modalYardSpots,
    warehouseData: modalWarehouseData,
    zones: modalZones,
    taskBasedLockedSpots: modalLockedSpots
  } = useYardData(undefined, 60000);

  const AVAILABLE_TASKS_TAB = 0;
  const MY_TASKS_TAB = 1;
  // Tab state (0 = unassigned, 1 = my tasks)
  const [selectedTab, setSelectedTab] = useState<number>(0);

  // Filter state for "My Tasks" ('active' or 'archived')
  const [tasksFilter, setTasksFilter] = useState<TaskFilter>(FILTER_ACTIVE);

  // Archived tasks state (lazy-loaded separately)
  const [archivedTasks, setArchivedTasks] = useState<Task[]>([]);
  const [archivedDays, setArchivedDays] = useState<number>(7);
  const [isLoadingArchived, setIsLoadingArchived] = useState<boolean>(false);
  const [archivedLoaded, setArchivedLoaded] = useState<boolean>(false);

  // Checklist completion state - maps task UUID to single boolean value
  const [checklistCompletionState, setChecklistCompletionState] = useState<Record<string, boolean>>({});

  // Track if user has active tasks
  const [userHasActiveTasks, setUserHasActiveTasks] = useState<boolean>(false);

  // Trailer notes state
  const [trailerNotesValue, setTrailerNotesValue] = useState<string>('');
  const [isEditingTrailerNotes, setIsEditingTrailerNotes] = useState<boolean>(false);
  const [savingTrailerNotes, setSavingTrailerNotes] = useState<boolean>(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Drag and drop state
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverTask, setDragOverTask] = useState<Task | null>(null);
  
  // Prioritize mode state
  const [isPrioritizeMode, setIsPrioritizeMode] = useState<boolean>(false);
  const [reorderedTasks, setReorderedTasks] = useState<Task[]>([]);
  const [isSavingPriorities, setIsSavingPriorities] = useState<boolean>(false);
  
  // Get search parameter from URL
  const getSearchParamFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('group') || params.get('search') || '';
  };

  // Helper function to check if a string looks like a UUID
  const isUUID = (str: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Helper function to recursively search through an object
  const searchInObject = (obj: any, searchTerm: string): boolean => {
    if (!obj || !searchTerm) return false;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    if (typeof obj === 'string') {
      // Skip partial UUID matches - only match if search term is also a UUID and it's an exact match
      if (isUUID(obj)) {
        return isUUID(searchTerm) && obj.toLowerCase() === lowerSearchTerm;
      }
      return obj.toLowerCase().includes(lowerSearchTerm);
    }
    
    if (typeof obj === 'number') {
      return obj.toString().includes(searchTerm);
    }
    
    if (Array.isArray(obj)) {
      return obj.some(item => searchInObject(item, searchTerm));
    }
    
    if (typeof obj === 'object') {
      return Object.values(obj).some(value => searchInObject(value, searchTerm));
    }
    
    return false;
  };

  // Initialize search from URL parameter
  useEffect(() => {
    const searchParam = getSearchParamFromUrl();
    if (searchParam) {
      setSearchTerm(searchParam);
    }
  }, [location.search]);

  // Handle search clear
  const handleClearSearch = () => {
    setSearchTerm('');
    // Remove search parameters from URL
    const params = new URLSearchParams(location.search);
    params.delete('group');
    params.delete('search');
    const newSearch = params.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  // Fetch facilities using GraphQL
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
              facility_alias
              configuration
              campus {
                uuid
                name
              }
            }
          }
        `;

        // Get auth token from cookie
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, set default
            if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
        setError('Failed to fetch facilities');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFacilities();
  }, []);

  // Fetch unassigned tasks using GraphQL
  const fetchUnassignedTasks = async () => {
    if (!selectedFacility) return;
    
    try {
      setIsLoadingData(true);
      setError(null);

      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for unassigned tasks by facility
      const tasksQuery = `
        query GetUnassignedTasksByCampus($facilityId: ID!) {
          unassigned_tasks_by_campus(facility_id: $facilityId) {
            tasks {
              uuid
              data
              due_at
              completed_at
              user_id
              facility_id
              facility {
                id
                name
                facility_alias
                configuration
              }
              source_facility_id
              cross_facility
              requires_destination_selection
              driver_id
              completed_by_user_id
              completed_by_driver_id
              modified_by_user_id
              metadata
              is_paused
              created_at
              updated_at


              task_type {
                uuid
                type
                class
              }
              
              task_status {
                uuid
                status
              }
              
              from_location
              to_location
              from_object {
                __typename
                ... on DockObject {
                  uuid
                  name
                  warehouseZone {
                    name
                  }
                }
                ... on YardSpotObject {
                  uuid
                  name
                  facilityZoneUuid
                  facilityZone {
                    name
                  }
                }
                ... on ExternalLocationObject {
                  name
                }
                ... on FacilityZoneObject {
                  uuid
                  name
                }
              }
              to_object {
                __typename
                ... on YardSpotObject {
                  uuid
                  name
                  facilityZone {
                    name
                  }
                }
                ... on DockObject {
                  uuid
                  name
                  warehouseZone {
                    name
                  }
                }
                ... on WarehouseZoneObject {
                  uuid
                  name
                }
                ... on ExternalLocationObject {
                  name
                }
                ... on FacilityZoneObject {
                  uuid
                  name
                }
              }
              trailer {
                trailer_number
                carrier {
                  name
                }
                active_lineage {
                  uuid
                  notes
                }
              }

              # ADD THIS FIELD FOR SAFETY CHECK TASKS
              related_movement_task {
                uuid
                from_location
                to_location
                from_object {
                  __typename
                  ... on DockObject {
                    uuid
                    name
                    warehouseZone {
                      name
                    }
                  }
                  ... on YardSpotObject {
                    uuid
                    name
                    facilityZoneUuid
                    facilityZone {
                      name
                    }
                  }
                  ... on ExternalLocationObject {
                    name
                  }
                  ... on FacilityZoneObject {
                    uuid
                    name
                  }
                }
                to_object {
                  __typename
                  ... on YardSpotObject {
                    uuid
                    name
                    facilityZone {
                      name
                    }
                  }
                  ... on DockObject {
                    uuid
                    name
                    warehouseZone {
                      name
                    }
                  }
                  ... on WarehouseZoneObject {
                    uuid
                    name
                  }
                  ... on ExternalLocationObject {
                    name
                  }
                  ... on FacilityZoneObject {
                    uuid
                    name
                  }
                }
              }
            }
          }
        }
      `;

      // Execute the GraphQL query
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: tasksQuery,
          variables: {
            facilityId: selectedFacility?.id?.toString() || "1"
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const rawTasks = (result.data.unassigned_tasks_by_campus.tasks || []).map((task: any) => ({
        ...task,
        trailer: task.trailer ? {
          ...task.trailer,
          carrier_name: task.trailer.carrier?.name || task.trailer.carrier_name,
          lineage_uuid: task.trailer.active_lineage?.uuid || task.trailer.lineage_uuid,
          lineage_notes: task.trailer.active_lineage?.notes || task.trailer.lineage_notes,
        } : task.trailer,
      }));
      setTasks(rawTasks);

      // Initialize checklist state for workflow tasks
      initializeWorkflowTasksState(rawTasks);

    } catch (err) {
      console.error('Failed to fetch unassigned tasks:', err);
      setError('Failed to fetch unassigned tasks');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch current user tasks using GraphQL
  const fetchCurrentUserTasks = async () => {
    try {
      setIsLoadingData(true);
      setError(null);

      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for current user tasks (flattened single-query endpoint)
      const tasksQuery = `
        query GetCurrentUserTaskViews {
          current_user_task_views {
            tasks {
              uuid
              data
              due_at
              completed_at
              user_id
              facility_id
              facility {
                id
                name
                facility_alias
                configuration
              }
              source_facility_id
              cross_facility
              requires_destination_selection
              driver_id
              completed_by_user_id
              completed_by_driver_id
              modified_by_user_id
              metadata
              is_paused
              created_at
              updated_at

              task_type { uuid type class }
              task_status { uuid status }

              from_location
              to_location
              from_object { type uuid name facility_zone_uuid facility_zone_name }
              to_object { type uuid name facility_zone_uuid facility_zone_name }

              trailer {
                trailer_number
                carrier_name
                lineage_uuid
                lineage_notes
              }

              related_movement_task {
                uuid
                from_location
                to_location
                from_object { type uuid name facility_zone_uuid facility_zone_name }
                to_object { type uuid name facility_zone_uuid facility_zone_name }
              }
            }
          }
        }
      `;

      // Execute the GraphQL query
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: tasksQuery
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const userTasks = result.data.current_user_task_views.tasks || [];
      setTasks(userTasks);

      // Server now only returns active tasks (excludes completed/cancelled)
      const hasActive = userTasks.length > 0;
      setUserHasActiveTasks(hasActive);

      // Initialize checklist state for workflow tasks
      initializeWorkflowTasksState(userTasks);

      return hasActive; // Return whether there are active tasks
    } catch (err) {
      console.error('Failed to fetch current user tasks:', err);
      setError('Failed to fetch current user tasks');
      return true; // Return true on error to avoid switching tabs
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fetch archived tasks from server (lazy-loaded)
  const fetchArchivedTasks = async (days: number = archivedDays) => {
    try {
      setIsLoadingArchived(true);
      setError(null);

      const archived = await fetchArchivedUserTasks(days);
      setArchivedTasks(archived);
      setArchivedLoaded(true);
    } catch (err) {
      console.error('Failed to fetch archived tasks:', err);
      setError('Failed to fetch archived tasks');
    } finally {
      setIsLoadingArchived(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    // Reset filter and archived state when switching main tabs
    setTasksFilter(FILTER_ACTIVE);
    setArchivedTasks([]);
    setArchivedLoaded(false);
    // Exit prioritize mode when switching tabs
    if (isPrioritizeMode) {
      setIsPrioritizeMode(false);
      setReorderedTasks([]);
    }
  };

  // Handle tasks filter change
  const handleTasksFilterChange = (event: any) => {
    const newFilter = event.target.value;
    setTasksFilter(newFilter);
    // Lazy-load archived tasks when first switching to archived view
    if (newFilter === FILTER_ARCHIVED && !archivedLoaded) {
      fetchArchivedTasks();
    }
  };

  // Handle archived days change
  const handleArchivedDaysChange = (event: any) => {
    const newDays = event.target.value;
    setArchivedDays(newDays);
    fetchArchivedTasks(newDays);
  };

  // Fetch appropriate data based on selected tab
  const fetchTasksData = async () => {
    if (selectedTab === AVAILABLE_TASKS_TAB) {
      await fetchUnassignedTasks();
      return true; // Not relevant for Available Tasks tab
    } else {
      return await fetchCurrentUserTasks();
    }
  };

  // Initialize checklist state for workflow tasks after data is loaded
  const initializeWorkflowTasksState = (loadedTasks: Task[]) => {
    // Clean up state for tasks that are no longer present
    const currentTaskUuids = new Set(loadedTasks.map(task => task.uuid));
    setChecklistCompletionState(prev => {
      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach(taskUuid => {
        if (currentTaskUuids.has(taskUuid)) {
          newState[taskUuid] = prev[taskUuid];
        }
      });
      return newState;
    });

    // Initialize state for new workflow tasks with checklists
    loadedTasks.forEach(task => {
      if (isWorkflowWithChecklist(task) && checklistCompletionState[task.uuid] === undefined) {
        initializeChecklistState(task.uuid);
      }
    });
  };

  // Check for active tasks on mount
  useEffect(() => {
    const checkActiveTasksOnMount = async () => {
      try {
        // Get auth token from cookie
        const authToken = getAuthToken();

        // GraphQL query for current user tasks (flattened endpoint)
        const tasksQuery = `
          query GetCurrentUserTaskViews {
            current_user_task_views {
              tasks {
                uuid
                task_status { status }
              }
            }
          }
        `;

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: tasksQuery
          })
        });

        const result = await response.json();

        if (!result.errors) {
          const userTasks = result.data.current_user_task_views.tasks || [];
          // Server now only returns active tasks (excludes completed/cancelled)
          setUserHasActiveTasks(userTasks.length > 0);
        }
      } catch (err) {
        console.error('Failed to check active tasks on mount:', err);
      }
    };

    checkActiveTasksOnMount();
  }, []);

  // Fetch data when facility or tab changes
  useEffect(() => {
    if (selectedFacility && selectedTab === AVAILABLE_TASKS_TAB) {
      fetchUnassignedTasks();
      fetchAllFacilityTasks(); // Fetch all tasks for group validation
    } else if (selectedTab === MY_TASKS_TAB) {
      fetchCurrentUserTasks();
      fetchAllFacilityTasks(); // Fetch all tasks for group validation
    }
  }, [selectedFacility, selectedTab]);
  
  // Handle facility change
  const handleFacilityChange = (event: any) => {
    const facilityId = event.target.value;
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setSelectedFacility(facility);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchTasksData();
  };

  // Auto-refresh every 30 seconds when on Available Tasks tab and not prioritizing
  useEffect(() => {
    // Only set up interval if on Available Tasks tab and not in prioritize mode
    if (selectedTab === AVAILABLE_TASKS_TAB && !isPrioritizeMode) {
      const intervalId = setInterval(() => {
        handleRefresh();
      }, 30000); // 30 seconds

      // Cleanup interval on unmount or when conditions change
      return () => clearInterval(intervalId);
    }
  }, [selectedTab, isPrioritizeMode]);

  // Get priority value (default to 'normal' if null/undefined)
  const getTaskPriority = (task: Task): string => {
    return task.data?.priority || 'normal';
  };

  // Get priority for a task (numeric value for sorting, default to 0 if not set)
  const getSortedPriority = (task: Task): number => {
    const priority = task.data?.priority;
    // If it's already a number, return it
    if (typeof priority === 'number') {
      return priority;
    }
    // Otherwise return 0 (for old string-based priorities or missing values)
    return 0;
  };

  // Group tasks by task_group_uuid for unassigned tasks
  const getGroupedTasks = () => {
    const filteredTasks = getFilteredTasks();
    
    if (selectedTab !== 0) {
      // For "My Tasks" tab, don't group - show all tasks
      return filteredTasks;
    }
    
    // Group unassigned tasks by task_group_uuid while preserving API order
    const taskGroups: Record<string, Task[]> = {};
    const groupOrder: string[] = []; // Track the order groups are first encountered
    const ungroupedTasks: Task[] = [];
    
    filteredTasks.forEach(task => {
      const taskGroupUuid = task.data?.task_group_uuid;
      if (taskGroupUuid) {
        if (!taskGroups[taskGroupUuid]) {
          taskGroups[taskGroupUuid] = [];
          groupOrder.push(taskGroupUuid); // Track order of first occurrence
        }
        taskGroups[taskGroupUuid].push(task);
      } else {
        ungroupedTasks.push(task);
      }
    });
    
    // For grouped tasks, only show MOVE_TRAILER task (if it exists), otherwise show first task
    // Process groups in the order they were first encountered to preserve API order
    const displayTasks: Task[] = [];
    
    groupOrder.forEach(groupUuid => {
      const group = taskGroups[groupUuid];
      if (group.length === 1) {
        // Single task, show it
        displayTasks.push(group[0]);
      } else {
        // Multiple tasks, prefer MOVE_TRAILER
        const moveTrailerTask = group.find(task => task.task_type?.type === 'MOVE_TRAILER');
        displayTasks.push(moveTrailerTask || group[0]);
      }
    });
    
    // Add ungrouped tasks
    displayTasks.push(...ungroupedTasks);
    
    // Sort by priority (descending, higher numbers first), then by created_at
    return displayTasks.sort((a, b) => {
      // First sort by priority (higher values first)
      const aPriority = getSortedPriority(a);
      const bPriority = getSortedPriority(b);
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }
      
      // Then by created_at (oldest first for same priority)
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  };

  // Apply search filtering to a list of tasks
  const applySearchFilter = (taskList: Task[]): Task[] => {
    if (!searchTerm) return taskList;

    if (isUUID(searchTerm)) {
      return taskList.filter(task =>
        task.data?.task_group_uuid === searchTerm || task.uuid === searchTerm
      );
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return taskList.filter(task => {
      if (searchInObject(task.data, searchTerm)) return true;
      if (task.trailer?.trailer_number?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.trailer?.carrier_name?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.task_type?.type?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.task_status?.status?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.from_location?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.to_location?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.from_object && 'name' in task.from_object &&
          task.from_object.name?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.to_object && 'name' in task.to_object &&
          task.to_object.name?.toLowerCase().includes(lowerSearchTerm)) return true;
      if (task.task_template?.name?.toLowerCase().includes(lowerSearchTerm)) return true;
      return false;
    });
  };

  // Filter tasks based on tab, filter, and search
  const getFilteredTasks = () => {
    if (selectedTab === MY_TASKS_TAB && tasksFilter === FILTER_ARCHIVED) {
      // Archived tasks come from a separate server query
      return applySearchFilter(archivedTasks);
    }
    // Active tasks and unassigned tasks come from the main tasks list
    return applySearchFilter(tasks);
  };

  // Get grouped tasks without sorting - use API order
  const groupedTasks = getGroupedTasks();
  
  // The facility filter keeps a task filed under, or moving a trailer out of, a selected facility.
  // Prioritize ignores it: priorities are saved over every campus task, so all of them stay in the list.
  const facilityFilterApplies = facilityFilter.isFiltered && selectedTab === AVAILABLE_TASKS_TAB && !isPrioritizeMode;
  const visibleTasks = facilityFilterApplies
    ? groupedTasks.filter(task => taskMatchesFacilityFilter(task, facilityFilter.ids))
    : groupedTasks;

  // Use reordered tasks when in prioritize mode, otherwise use grouped tasks
  const displayTasks = isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB ? reorderedTasks : visibleTasks;

  // Multi-facility campus: Available tasks are sectioned by filing facility, in campus order.
  const groupTasksByFacility = (list: Task[]): Array<{ key: string; label: string | null; tasks: Task[] }> => {
    if (!isMultiFacility || selectedTab !== AVAILABLE_TASKS_TAB || isPrioritizeMode) {
      return [{ key: 'all', label: null, tasks: list }];
    }
    const anchorId = String(selectedFacility?.id ?? '');
    const order = [anchorId, ...campusFacilities.map(f => String(f.id)).filter(id => id !== anchorId)];
    const byFacility = new Map<string, Task[]>();
    list.forEach(task => {
      const id = String(task.facility?.id ?? task.facility_id);
      byFacility.set(id, [...(byFacility.get(id) || []), task]);
    });
    const ids = [...order.filter(id => byFacility.has(id)), ...Array.from(byFacility.keys()).filter(id => !order.includes(id))];
    return ids.map(id => ({
      key: id,
      label:
        facilityDisplayName(campusFacilities.find(f => String(f.id) === id)) ||
        facilityDisplayName(byFacility.get(id)![0].facility) ||
        `Facility ${id}`,
      tasks: byFacility.get(id)!,
    }));
  };

  // Get status color
  const getStatusColor = (status: string | undefined): "default" | "success" | "primary" | "error" | "secondary" | "info" | "warning" | undefined => {
    if (!status) return 'default';
    
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'active':
        return 'primary';
      case 'pending':
      case 'unassigned':
        return 'warning';
      case 'paused':
        return 'default';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Handle view task details
  const handleViewTask = (task: Task) => {
    setSelectedTask(task);
    setIsTaskDetailModalOpen(true);
    
    // Initialize trailer notes value
    setTrailerNotesValue(task.trailer?.lineage_notes || '');
    setIsEditingTrailerNotes(false);
    
    // Initialize checklist state if it's a workflow with checklist
    if (isWorkflowWithChecklist(task)) {
      initializeChecklistState(task.uuid);
    }
  };

  // Handle save trailer notes
  const handleSaveTrailerNotes = async () => {
    if (!selectedTask?.data?.trailer_uuid) return;
    
    setSavingTrailerNotes(true);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL mutation to update trailer notes
      const updateTrailerMutation = `
        mutation UpdateTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
            trailer_number
            active_lineage {
              uuid
              notes
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            uuid: selectedTask.data.trailer_uuid,
            input: {
              notes: trailerNotesValue
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Update the selected task with the new trailer notes immediately for UI feedback
      if (selectedTask.trailer) {
        const updatedTask = {
          ...selectedTask,
          trailer: {
            ...selectedTask.trailer,
            lineage_uuid: result.data.updateTrailer.active_lineage?.uuid || selectedTask.trailer.lineage_uuid,
            lineage_notes: trailerNotesValue
          }
        };
        setSelectedTask(updatedTask);
      }
      
      // Exit edit mode
      setIsEditingTrailerNotes(false);
      
      // Refresh tasks data to ensure consistency
      await fetchTasksData();
      
    } catch (error: any) {
      console.error('Failed to save trailer notes:', error);
      setError('Failed to save trailer notes');
    } finally {
      setSavingTrailerNotes(false);
    }
  };

  // Handle complete regular task (non-movement)
  const handleCompleteTask = async (taskUuid: string) => {
    setProcessingTasks(prev => new Set(prev).add(taskUuid));
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const completeTaskMutation = `
        mutation CompleteTask($taskUuid: String!) {
          complete_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeTaskMutation,
          variables: { taskUuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh tasks data and check if there are active tasks remaining
      const hasActiveTasks = await fetchTasksData();
      
      // If on "My Tasks" tab with active filter and no active tasks left, switch to Available Tasks
      if (selectedTab === MY_TASKS_TAB && tasksFilter === FILTER_ACTIVE && !hasActiveTasks) {
        setSelectedTab(AVAILABLE_TASKS_TAB); // Switch to Available Tasks tab
      }
      
      // Close modal if this task was being viewed
      if (selectedTask?.uuid === taskUuid) {
        setIsTaskDetailModalOpen(false);
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
      setError('Failed to complete task');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        return newSet;
      });
    }
  };

  // Handle complete movement task directly (no confirmation modal)
  const handleCompleteMovementTask = async (taskUuid: string) => {
    setProcessingTasks(prev => new Set(prev).add(taskUuid));

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('authToken='))
        ?.split('=')[1] || localStorage.getItem('authToken');

      const completeMovementTaskMutation = `
        mutation CompleteMovementTask($input: CompleteMovementTaskInput!) {
          completeMovementTask(input: $input) {
            uuid
            taskStatus {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeMovementTaskMutation,
          variables: { input: { taskUuid } }
        })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const hasActiveTasks = await fetchTasksData();

      if (selectedTab === MY_TASKS_TAB && tasksFilter === FILTER_ACTIVE && !hasActiveTasks) {
        setSelectedTab(AVAILABLE_TASKS_TAB);
      }

      if (selectedTask?.uuid === taskUuid) {
        setIsTaskDetailModalOpen(false);
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Failed to complete movement task:', error);
      setError(error instanceof Error ? error.message : 'Failed to complete movement task');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        return newSet;
      });
    }
  };

  // Handle opening the change destination modal for a movement task
  const handleChangeDestination = (taskUuid: string) => {
    const task = [...tasks, ...allFacilityTasks].find(t => t.uuid === taskUuid);
    if (task) {
      setTaskToComplete(task);
      setConfirmationModalOpen(true);
    }
  };
  
  // Handle movement task completion with destination overrides
  const handleCompleteMovementTaskWithDestination = async (destinationOverrides: any) => {
    if (!taskToComplete) return;
    
    const taskUuid = taskToComplete.uuid;
    setProcessingTasks(prev => new Set(prev).add(taskUuid));
    setConfirmationModalOpen(false);
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const completeMovementTaskMutation = `
        mutation CompleteMovementTask($input: CompleteMovementTaskInput!) {
          completeMovementTask(input: $input) {
            uuid
            taskStatus {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeMovementTaskMutation,
          variables: { 
            input: { 
              taskUuid: taskUuid,
              ...destinationOverrides 
            } 
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh tasks data and check if there are active tasks remaining
      const hasActiveTasks = await fetchTasksData();
      
      // If on "My Tasks" tab with active filter and no active tasks left, switch to Available Tasks
      if (selectedTab === MY_TASKS_TAB && tasksFilter === FILTER_ACTIVE && !hasActiveTasks) {
        setSelectedTab(AVAILABLE_TASKS_TAB); // Switch to Available Tasks tab
      }
      
      // Close modal if this task was being viewed
      if (selectedTask?.uuid === taskUuid) {
        setIsTaskDetailModalOpen(false);
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Failed to complete movement task:', error);
      setError('Failed to complete movement task');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        return newSet;
      });
      setTaskToComplete(null);
    }
  };

  // Handle assign task to current user
  const handleAssignTask = async (taskUuid: string) => {
    setProcessingTasks(prev => new Set(prev).add(taskUuid));
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      // Find the task being assigned
      const taskToAssign = tasks.find(t => t.uuid === taskUuid);
      
      // Check if this task has a task_group_uuid and if there are other tasks with the same task_group_uuid
      const taskGroupUuid = taskToAssign?.data?.task_group_uuid;
      let tasksToAssign = [taskUuid];
      
      if (taskGroupUuid && selectedTab === AVAILABLE_TASKS_TAB) {
        // Find all unassigned tasks with the same task_group_uuid
        const relatedTasks = tasks.filter(task => 
          task.data?.task_group_uuid === taskGroupUuid &&
          task.uuid !== taskUuid // Don't include the main task we're already assigning
        );
        
        tasksToAssign = [taskUuid, ...relatedTasks.map(t => t.uuid)];
        
        // Add related task UUIDs to processing state
        relatedTasks.forEach(task => {
          setProcessingTasks(prev => new Set(prev).add(task.uuid));
        });
      }

      const assignTaskMutation = `
        mutation AssignTaskToCurrentUser($uuid: String!) {
          assign_task_to_current_user(uuid: $uuid) {
            uuid
            user_id
            task_status {
              status
            }
          }
        }
      `;

      // Assign all tasks
      const assignmentPromises = tasksToAssign.map(uuid => 
        fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: assignTaskMutation,
            variables: { uuid }
          })
        }).then(response => response.json())
      );

      const results = await Promise.all(assignmentPromises);
      
      // Check for errors in any of the assignments
      const errors = results.filter(result => result.errors);
      if (errors.length > 0) {
        throw new Error(errors[0].errors[0].message);
      }

      // Fetch current user tasks FIRST before switching tabs to avoid showing stale data
      await fetchCurrentUserTasks();
      
      // Initialize checklist state for newly assigned workflow tasks
      tasksToAssign.forEach(uuid => {
        const assignedTask = tasks.find(t => t.uuid === uuid);
        if (assignedTask && isWorkflowWithChecklist(assignedTask)) {
          initializeChecklistState(uuid);
        }
      });
      
      // Navigate to My Tasks tab AFTER data is loaded
      setSelectedTab(MY_TASKS_TAB);
      setTasksFilter(FILTER_ACTIVE); // Reset filter to active when switching to My Tasks
      
      // Close modal if this task was being viewed
      if (selectedTask?.uuid === taskUuid) {
        setIsTaskDetailModalOpen(false);
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Failed to assign task:', error);
      setError('Failed to assign task');
    } finally {
      // Remove all assigned tasks from processing state
      const taskToAssign = tasks.find(t => t.uuid === taskUuid);
      const taskGroupUuid = taskToAssign?.data?.task_group_uuid;
      
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        
        if (taskGroupUuid && selectedTab === AVAILABLE_TASKS_TAB) {
          // Remove related tasks from processing state
          tasks.forEach(task => {
            if (task.data?.task_group_uuid === taskGroupUuid) {
              newSet.delete(task.uuid);
            }
          });
        }
        
        return newSet;
      });
    }
  };

  // Handle toggle task pause/unpause
  const handleToggleTaskPause = async (taskUuid: string) => {
    setProcessingTasks(prev => new Set(prev).add(taskUuid));

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('authToken='))
        ?.split('=')[1] || localStorage.getItem('authToken');

      const task = tasks.find(t => t.uuid === taskUuid);
      const newPausedState = !task?.is_paused;

      const togglePauseMutation = `
        mutation ToggleTaskPause($uuid: String!, $isPaused: Boolean!) {
          toggle_task_pause(uuid: $uuid, is_paused: $isPaused) {
            uuid
            is_paused
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: togglePauseMutation,
          variables: { uuid: taskUuid, isPaused: newPausedState }
        })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Update the task in local state
      setTasks(prev => prev.map(t =>
        t.uuid === taskUuid ? { ...t, is_paused: newPausedState } : t
      ));

      // Update selected task if viewing it in modal
      if (selectedTask?.uuid === taskUuid) {
        setSelectedTask(prev => prev ? { ...prev, is_paused: newPausedState } : prev);
      }
    } catch (error) {
      console.error('Failed to toggle task pause:', error);
      setError('Failed to toggle task pause status');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        return newSet;
      });
    }
  };

  // Handle unassign task
  const handleUnassignTask = async (taskUuid: string) => {
    setProcessingTasks(prev => new Set(prev).add(taskUuid));
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      // Find the task being unassigned
      const taskToUnassign = tasks.find(t => t.uuid === taskUuid);
      
      // Check if this task has a task_group_uuid and if there are other tasks with the same task_group_uuid
      const taskGroupUuid = taskToUnassign?.data?.task_group_uuid;
      let tasksToUnassign = [taskUuid];
      
      if (taskGroupUuid && selectedTab === MY_TASKS_TAB) {
        // Find all assigned tasks with the same task_group_uuid
        const relatedTasks = tasks.filter(task => 
          task.data?.task_group_uuid === taskGroupUuid &&
          task.uuid !== taskUuid // Don't include the main task we're already unassigning
        );
        
        tasksToUnassign = [taskUuid, ...relatedTasks.map(t => t.uuid)];
        
        // Add related task UUIDs to processing state
        relatedTasks.forEach(task => {
          setProcessingTasks(prev => new Set(prev).add(task.uuid));
        });
      }

      const unassignTaskMutation = `
        mutation UnassignTask($uuid: String!) {
          unassign_task(uuid: $uuid) {
            uuid
            user_id
            task_status {
              status
            }
          }
        }
      `;

      // Unassign all tasks
      const unassignmentPromises = tasksToUnassign.map(uuid => 
        fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: unassignTaskMutation,
            variables: { uuid }
          })
        }).then(response => response.json())
      );

      const results = await Promise.all(unassignmentPromises);
      
      // Check for errors in any of the unassignments
      const errors = results.filter(result => result.errors);
      if (errors.length > 0) {
        throw new Error(errors[0].errors[0].message);
      }

      // Refresh tasks data
      await fetchTasksData();
      
      // Update active tasks status after unassigning
      if (selectedTab === MY_TASKS_TAB) {
        // After unassigning, we need to check if user still has active tasks
        const remainingTasks = tasks.filter(t => !tasksToUnassign.includes(t.uuid));
        const hasActive = remainingTasks.some((task: Task) => {
          const status = task.task_status?.status?.toLowerCase();
          return status !== 'cancelled' && status !== 'completed';
        });
        setUserHasActiveTasks(hasActive);
      }
    } catch (error) {
      console.error('Failed to unassign task:', error);
      setError('Failed to unassign task');
    } finally {
      // Remove all unassigned tasks from processing state
      const taskToUnassign = tasks.find(t => t.uuid === taskUuid);
      const taskGroupUuid = taskToUnassign?.data?.task_group_uuid;
      
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        
        if (taskGroupUuid && selectedTab === MY_TASKS_TAB) {
          // Remove related tasks from processing state
          tasks.forEach(task => {
            if (task.data?.task_group_uuid === taskGroupUuid) {
              newSet.delete(task.uuid);
            }
          });
        }
        
        return newSet;
      });
    }
  };

  // Handle cancel task
  const handleCancelTask = async (taskUuid: string) => {
    setProcessingTasks(prev => new Set(prev).add(taskUuid));
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const cancelTaskMutation = `
        mutation CancelTask($taskUuid: String!) {
          cancel_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: cancelTaskMutation,
          variables: { taskUuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh tasks data
      await fetchTasksData();
      
      // Close modal if this task was being viewed
      if (selectedTask?.uuid === taskUuid) {
        setIsTaskDetailModalOpen(false);
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Failed to cancel task:', error);
      setError('Failed to cancel task');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskUuid);
        return newSet;
      });
    }
  };

  // Handle close task detail modal
  const handleCloseTaskDetail = () => {
    setIsTaskDetailModalOpen(false);
    setSelectedTask(null);
  };

  // Get priority color for badge
  const getPriorityColor = (priority: string): "default" | "success" | "primary" | "error" | "secondary" | "info" | "warning" | undefined => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error';
      case 'low':
        return 'info';
      case 'normal':
      default:
        return 'default';
    }
  };

  // Handle priority change (plus/minus buttons)
  const handlePriorityChange = async (taskUuid: string, direction: 'up' | 'down') => {
    const task = tasks.find(t => t.uuid === taskUuid);
    if (!task) return;

    // Find all tasks in the same group
    const taskGroupUuid = task.data?.task_group_uuid;
    const relatedTasks = taskGroupUuid 
      ? tasks.filter(t => t.data?.task_group_uuid === taskGroupUuid)
      : [task];

    // Add all related tasks to processing state
    setProcessingTasks(prev => {
      const newSet = new Set(prev);
      relatedTasks.forEach(t => newSet.add(t.uuid));
      return newSet;
    });
    
    try {
      const currentPriority = getTaskPriority(task);
      let newPriority: string;

      if (direction === 'up') {
        // low -> normal -> high -> high (cycle stops at high)
        if (currentPriority === 'low') newPriority = 'normal';
        else if (currentPriority === 'normal') newPriority = 'high';
        else newPriority = 'high'; // already high, stay high
      } else {
        // high -> normal -> low -> low (cycle stops at low)
        if (currentPriority === 'high') newPriority = 'normal';
        else if (currentPriority === 'normal') newPriority = 'low';
        else newPriority = 'low'; // already low, stay low
      }

      // Don't make API call if priority wouldn't change
      if (newPriority === currentPriority) {
        return;
      }

      // Get auth token from cookie
      const authToken = getAuthToken();

      const updatePriorityMutation = `
        mutation UpdateTaskPriority($uuid: String!, $priority: String!) {
          update_task_priority(uuid: $uuid, priority: $priority) {
            uuid
            data
          }
        }
      `;

      // Update priority for all tasks in the group
      const updatePromises = relatedTasks.map(relatedTask => 
        fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: updatePriorityMutation,
            variables: {
              uuid: relatedTask.uuid,
              priority: newPriority
            }
          })
        })
      );

      const results = await Promise.all(updatePromises);
      
      // Check for errors in any of the updates
      for (const response of results) {
        const result = await response.json();
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }
      }

      // Refresh tasks data to reflect the change
      if (selectedTab === AVAILABLE_TASKS_TAB) {
        await fetchUnassignedTasks();
      } else {
        await fetchCurrentUserTasks();
      }

    } catch (error) {
      console.error('Failed to update task priority:', error);
      setError('Failed to update task priority');
    } finally {
      setProcessingTasks(prev => {
        const newSet = new Set(prev);
        relatedTasks.forEach(t => newSet.delete(t.uuid));
        return newSet;
      });
    }
  };

  // Enter prioritize mode
  const handleEnterPrioritizeMode = () => {
    setIsPrioritizeMode(true);
    setReorderedTasks([...groupedTasks]);
  };

  // Exit prioritize mode without saving
  const handleCancelPrioritizeMode = () => {
    setIsPrioritizeMode(false);
    setReorderedTasks([]);
  };

  // Save priorities using stacked mutations
  const handleSavePriorities = async () => {
    // Confirm priority save
    const confirmed = window.confirm(
      `Are you sure you want to save the new task priorities? This will update ${reorderedTasks.length} task(s).`
    );
    
    if (!confirmed) {
      return;
    }

    setIsSavingPriorities(true);
    
    try {
      const authToken = getAuthToken();

      // Build stacked mutations dynamically
      const mutationFields: string[] = [];
      const variables: any = {};

      reorderedTasks.forEach((task, index) => {
        const priority = (reorderedTasks.length - index) * 1000; // Top task gets highest number
        const alias = `task${index}`;
        
        mutationFields.push(
          `${alias}: update_task_priority(uuid: $uuid${index}, priority: $priority${index}) {
            uuid
            data
          }`
        );
        
        variables[`uuid${index}`] = task.uuid;
        variables[`priority${index}`] = priority;
      });

      // Build variable definitions for the mutation
      const variableDefinitions = reorderedTasks.map((_, index) => 
        `$uuid${index}: String!, $priority${index}: Float!`
      ).join(', ');

      const stackedMutation = `
        mutation UpdateMultiplePriorities(${variableDefinitions}) {
          ${mutationFields.join('\n          ')}
        }
      `;

      console.log('Stacked mutation updating priorities:', variables);

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: stackedMutation,
          variables
        })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Exit prioritize mode and refresh data
      setIsPrioritizeMode(false);
      setReorderedTasks([]);
      await fetchTasksData();
    } catch (error) {
      console.error('Failed to save priorities:', error);
      setError('Failed to save priorities');
    } finally {
      setIsSavingPriorities(false);
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, task: Task) => {
    if (!isPrioritizeMode) return;
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, task: Task) => {
    if (!isPrioritizeMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTask(task);
  };

  // Handle drag leave
  const handleDragLeave = () => {
    if (!isPrioritizeMode) return;
    setDragOverTask(null);
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, dropTarget: Task) => {
    e.preventDefault();
    
    if (!draggedTask || draggedTask.uuid === dropTarget.uuid) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    if (!isPrioritizeMode) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // In prioritize mode, just reorder the local array
    const currentList = [...reorderedTasks];
    const draggedIndex = currentList.findIndex(t => t.uuid === draggedTask.uuid);
    const targetIndex = currentList.findIndex(t => t.uuid === dropTarget.uuid);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // Remove the dragged item and insert it at the target position
    const [removed] = currentList.splice(draggedIndex, 1);
    currentList.splice(targetIndex, 0, removed);

    setReorderedTasks(currentList);
    setDraggedTask(null);
    setDragOverTask(null);
  };

  // Get location name from object
  // A location that is a zone itself (no spot or dock), or off the yard entirely.
  const isZoneOnlyLocation = (locationObject: any) => {
    const kind = locationObject?.type || locationObject?.__typename || '';
    return /FacilityZone|WarehouseZone|ExternalLocation/.test(kind);
  };

  // Zone half of a location: the spot's/dock's zone, or the zone itself for a zone-only location.
  const getLocationZone = (locationObject: any): string | null => {
    if (!locationObject) return null;
    if (isZoneOnlyLocation(locationObject)) return locationObject.name || null;
    return (
      locationObject.facility_zone_name ||
      locationObject.facilityZone?.name ||
      locationObject.warehouseZone?.name ||
      null
    );
  };

  // Spot/dock half of a location ("Dock D67", "Spot 41"); null for a zone-only location.
  const getLocationSpot = (locationObject: any): string | null => {
    if (!locationObject || isZoneOnlyLocation(locationObject)) return null;
    const name = locationObject.name ?? locationObject.number;
    if (name === undefined || name === null || name === '') return null;
    const kind = locationObject.type || locationObject.__typename || '';
    if (/Dock/.test(kind)) return `Dock ${name}`;
    if (/YardSpot/.test(kind)) return `Spot ${name}`;
    return String(name);
  };

  const getLocationName = (locationObject: any) => {
    if (!locationObject) return 'Unknown';
    // The task view sends the zone flat; task queries nest it (facilityZone / warehouseZone).
    const zoneName =
      locationObject.facility_zone_name ||
      locationObject.facilityZone?.name ||
      locationObject.warehouseZone?.name;
    if (zoneName && locationObject.name) {
      return `${zoneName} ${locationObject.name}`;
    }
    if ('name' in locationObject) return locationObject.name;
    if ('number' in locationObject) return locationObject.number;
    return 'Unknown';
  };

  // Format workflow type (replace _ with space and title case)
  const formatWorkflowType = (workflowType: string) => {
    return workflowType
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  };

  // Format date and time using user's local browser timezone
  const formatDateTime = (dateString: string) => {
    try {
      // Server sends UTC but may omit timezone signifier - ensure it's treated as UTC
      let normalized = dateString;
      if (!normalized.endsWith('Z') && !normalized.includes('+') && !/[-+]\d{2}:\d{2}$/.test(normalized)) {
        normalized += 'Z';
      }
      const date = new Date(normalized);

      const dateOptions: Intl.DateTimeFormatOptions = {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      };

      return date.toLocaleString('en-US', dateOptions);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Check if task is a workflow with checklist
  const isWorkflowWithChecklist = (task: Task) => {
    return task.task_type?.type === 'WORKFLOW' && 
           task.data && 
           task.data.checklist && 
           Array.isArray(task.data.checklist) && 
           task.data.checklist.length > 0;
  };

  // Get abbreviated checklist for display
  const getAbbreviatedChecklist = (task: Task) => {
    if (!isWorkflowWithChecklist(task)) return null;
    const checklist = task.data.checklist;
    const firstItem = checklist[0];
    const remainingCount = checklist.length - 1;
    
    if (remainingCount > 0) {
      return `${firstItem}... (+${remainingCount} more)`;
    }
    return firstItem;
  };

  // Initialize checklist completion state for a task
  const initializeChecklistState = (taskUuid: string) => {
    if (checklistCompletionState[taskUuid] === undefined) {
      setChecklistCompletionState(prev => ({
        ...prev,
        [taskUuid]: false
      }));
    }
  };

  // Handle checkbox change for checklist completion
  const handleChecklistChange = (taskUuid: string, checked: boolean) => {
    setChecklistCompletionState(prev => ({
      ...prev,
      [taskUuid]: checked
    }));
  };

  // Check if checklist is completed for a task
  const isChecklistCompleted = (taskUuid: string) => {
    return checklistCompletionState[taskUuid] === true;
  };

  // Check if a workflow task can be completed
  const canCompleteWorkflowTask = (task: Task) => {
    if (!isWorkflowWithChecklist(task)) return true; // Non-checklist workflows can be completed normally
    return isChecklistCompleted(task.uuid);
  };

  // Check if user can take new tasks (only if they have no active tasks)
  const canTakeNewTasks = () => {
    return !userHasActiveTasks;
  };

  // Check if user has an active MOVE_TRAILER task
  const hasActiveMoveTrailerTask = () => {
    if (selectedTab !== 1) return false; // Only relevant for "My Tasks" tab
    
    return tasks.some((task: Task) => {
      const status = task.task_status?.status?.toLowerCase();
      const isActive = status !== 'cancelled' && status !== 'completed';
      return isActive && task.task_type?.type === 'MOVE_TRAILER';
    });
  };

  // Check if a task should be grayed out (disabled when there's an active MOVE_TRAILER task)
  const shouldGrayOutTask = (task: Task) => {
    if (selectedTab !== 1) return false; // Only for "My Tasks" tab
    if (task.task_type?.type === 'MOVE_TRAILER') return false; // Don't gray out MOVE_TRAILER tasks themselves
    
    const status = task.task_status?.status?.toLowerCase();
    const isActive = status !== 'cancelled' && status !== 'completed';
    
    return isActive && hasActiveMoveTrailerTask();
  };

  // Fetch ALL tasks in facility for group completion validation
  const fetchAllFacilityTasks = async () => {
    if (!selectedFacility) return;
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for ALL tasks by facility (same as Tasks.tsx)
      // Task groups can span the campus, so check every active task in it.
      const allTasksQuery = campus ? `
        query GetActiveTasksByCampus($campusUuid: ID!) {
          tasks_by_facility: active_tasks_by_campus(campus_uuid: $campusUuid) {
            tasks {
              uuid
              data
              completed_at
              task_status {
                status
              }
              task_type {
                type
              }
            }
          }
        }
      ` : `
        query GetTasksByFacility($facilityId: ID!) {
          tasks_by_facility(facility_id: $facilityId) {
            tasks {
              uuid
              data
              completed_at
              task_status {
                status
              }
              task_type {
                type
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: allTasksQuery,
          variables: campus
            ? { campusUuid: campus.uuid }
            : { facilityId: selectedFacility?.id?.toString() || "1" }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('Failed to fetch all facility tasks:', result.errors);
        return;
      }

      setAllFacilityTasks(result.data.tasks_by_facility.tasks || []);

    } catch (err) {
      console.error('Failed to fetch all facility tasks:', err);
    }
  };

  // Check if a workflow or safety check task can be cancelled
  const canCancelTask = (task: Task) => {
    // Only apply this validation to WORKFLOW and SAFETY_CHECK tasks
    if (task.task_type?.type !== 'WORKFLOW' && task.task_type?.type !== 'SAFETY_CHECK') {
      return true; // Other task types can always be cancelled
    }

    // If no task_group_uuid, task can be cancelled
    const taskGroupUuid = task.data?.task_group_uuid;
    if (!taskGroupUuid) {
      return true;
    }

    // Check if any task in the same group has been completed using ALL facility tasks
    const hasCompletedTaskInGroup = allFacilityTasks.some(facilityTask => {
      if (facilityTask.data?.task_group_uuid !== taskGroupUuid) {
        return false; // Not in the same group
      }
      
      const status = facilityTask.task_status?.status?.toLowerCase();
      return status === 'completed' || facilityTask.completed_at !== null;
    });

    // If any task in the group is completed, this workflow/safety check task cannot be cancelled
    return !hasCompletedTaskInGroup;
  };

  // Get tooltip message for cancel button
  const getCancelTooltipMessage = (task: Task) => {
    if (shouldGrayOutTask(task)) {
      return "Complete your MOVE_TRAILER task first";
    }
    
    if (!canCancelTask(task)) {
      return "Cannot cancel: a task in this group has already been completed";
    }
    
    return "";
  };

  // Loading state for initial facilities fetch
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading facilities...</Typography>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error}</Typography>
        <IconButton onClick={handleRefresh}>
          <Refresh />
        </IconButton>
      </div>
    );
  }

  return (
    <Box sx={{ 
      '& .MuiTypography-root': { 
        fontSize: (theme) => `calc(${theme.typography.fontSize}px * 1.5) !important` 
      },
      '& .MuiChip-root': { 
        fontSize: '1.2rem !important' 
      },
      '& .MuiButton-root': { 
        fontSize: '1.3rem !important' 
      },
      '& .MuiTableCell-root': { 
        fontSize: '1.3rem !important' 
      },
      '& .MuiInputBase-root': {
        fontSize: '1.3rem !important'
      }
    }}>
      <div className="p-2 sm:p-5">
        {/* Header */}
        <Box sx={{ mb: 2 }}>
        {/* Warning message when user has active tasks and is on Available Tasks tab */}
        {selectedTab === AVAILABLE_TASKS_TAB && userHasActiveTasks && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: 'warning.light', 
            borderRadius: 1,
            border: 1,
            borderColor: 'warning.main'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
              ⚠️ You have active tasks assigned to you. Please complete or cancel your current tasks before taking new ones.
            </Typography>
          </Box>
        )}

        
        
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 }, 
          alignItems: 'center', 
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Campus Selector - only when the user's facilities span several campuses.
                Facilities within the campus are narrowed by the facility filter beside it. */}
            {selectedTab === AVAILABLE_TASKS_TAB && campusSelectOptions.length > 1 && (
              <FormControl 
                variant="outlined" 
                size="small" 
                sx={{ 
                  minWidth: { xs: 120, sm: 200 },
                  maxWidth: { xs: 150, sm: 'none' }
                }}
              >
                <InputLabel>Campus</InputLabel>
                <Select
                  value={campusSelectOptions.find(option => option.campusUuid === selectedCampus)?.facilityId ?? ''}
                  onChange={handleFacilityChange}
                  label="Campus"
                >
                  {campusSelectOptions.map(option => (
                    <MenuItem key={option.campusUuid} value={option.facilityId}>
                      <Typography variant="body2" noWrap>
                        {option.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Facility filter - Available tasks in a multi-facility campus; outlined while it hides facilities */}
            {selectedTab === AVAILABLE_TASKS_TAB && isMultiFacility && (
              <CampusFacilityMultiSelect
                facilities={campusFacilities.map(f => ({ id: String(f.id), name: facilityDisplayName(f) }))}
                value={facilityFilter.ids}
                onChange={setFacilityFilterIds}
                label={facilityFilter.isFiltered ? 'Filtering by facility' : 'Facility filter'}
                sx={{
                  minWidth: { xs: 150, sm: 220 },
                  maxWidth: { xs: 200, sm: 320 },
                  ...(facilityFilter.isFiltered && {
                    '& .MuiOutlinedInput-root': { backgroundColor: '#fff8e1' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main', borderWidth: 2 },
                    '& .MuiInputLabel-root': { color: 'warning.dark', fontWeight: 600 }
                  })
                }}
              />
            )}

            {/* Tasks Filter - Only show for My Tasks tab */}
            {selectedTab === MY_TASKS_TAB && (
              <>
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: { xs: 100, sm: 150 },
                    maxWidth: { xs: 120, sm: 'none' }
                  }}
                >
                  <InputLabel>Filter</InputLabel>
                  <Select
                    value={tasksFilter}
                    onChange={handleTasksFilterChange}
                    label="Filter"
                  >
                    <MenuItem value="active">
                      <Typography variant="body2" noWrap>
                        Active Tasks
                      </Typography>
                    </MenuItem>
                    <MenuItem value="archived">
                      <Typography variant="body2" noWrap>
                        Archived Tasks
                      </Typography>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Days selector - Only show when viewing archived */}
                {tasksFilter === FILTER_ARCHIVED && (
                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: { xs: 80, sm: 120 },
                      maxWidth: { xs: 100, sm: 'none' }
                    }}
                  >
                    <InputLabel>Days</InputLabel>
                    <Select
                      value={archivedDays}
                      onChange={handleArchivedDaysChange}
                      label="Days"
                      disabled={isLoadingArchived}
                    >
                      {[7, 14, 30, 60, 90].map(d => (
                        <MenuItem key={d} value={d}>
                          <Typography variant="body2" noWrap>
                            Last {d} days
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}

            {/* Universal Search */}
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search tasks (UUID or text)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                minWidth: { xs: 180, sm: 250 },
                maxWidth: { xs: 200, sm: 'none' }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Search Tag Display */}
            {searchTerm && (
              <Chip
                label={isUUID(searchTerm) ? `Group: ${searchTerm}` : `Search: ${searchTerm}`}
                color="primary"
                size="small"
                onDelete={handleClearSearch}
                deleteIcon={<Clear />}
                sx={{ maxWidth: { xs: 150, sm: 'none' } }}
              />
            )}

            {/* Refresh Button */}
            <IconButton 
              onClick={handleRefresh} 
              disabled={isLoadingData}
              color="primary"
              size="small"
            >
              {isLoadingData ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Task Count */}
            <Chip 
              label={`${displayTasks.length}${facilityFilterApplies ? ` of ${groupedTasks.length}` : ''} ${selectedTab === AVAILABLE_TASKS_TAB ? 'unassigned' : tasksFilter === FILTER_ACTIVE ? 'active' : 'archived'}`} 
              color={selectedTab === AVAILABLE_TASKS_TAB ? "warning" : tasksFilter === FILTER_ACTIVE ? "primary" : "default"} 
              size="small"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
            />
            
            {/* Prioritize Mode Buttons (only show on unassigned tab) */}
            {selectedTab === AVAILABLE_TASKS_TAB && (
              <>
                {!isPrioritizeMode ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleEnterPrioritizeMode}
                    startIcon={<DragIndicator />}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                  >
                    Prioritize
                  </Button>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={handleSavePriorities}
                      disabled={isSavingPriorities}
                      startIcon={isSavingPriorities ? <CircularProgress size={16} /> : <CheckCircle />}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                    >
                      {isSavingPriorities ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={handleCancelPrioritizeMode}
                      disabled={isSavingPriorities}
                      startIcon={<Cancel />}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange}
          aria-label="task tabs"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              minWidth: { xs: 120, sm: 160 },
              padding: { xs: '8px 12px', sm: '12px 24px' }
            }
          }}
        >
          <Tab label={isMobile ? "Available" : "Available Tasks"} />
          <Tab label={isMobile ? "My Tasks" : "My Tasks"} />
        </Tabs>
      </Paper>

      {/* Facility filter banner - says plainly that campus tasks are hidden, and how to see them */}
      {selectedTab === AVAILABLE_TASKS_TAB && facilityFilter.isFiltered && (
        <Alert
          severity={isPrioritizeMode ? 'info' : 'warning'}
          sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-action': { pt: 0 } }}
          action={
            !isPrioritizeMode && (
              <Button color="inherit" size="small" onClick={() => setFacilityFilterIds(facilityFilter.allIds)}>
                Show all campus tasks
              </Button>
            )
          }
        >
          {isPrioritizeMode
            ? 'Facility filter paused while prioritizing: every campus task is shown.'
            : `Filtered to ${facilityFilter.names}.`}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxWidth: '100vw' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: { xs: 80, sm: 120 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Type
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 60, sm: 80 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Status
                  </Typography>
                </TableCell>
                <TableCell sx={{ 
                  minWidth: { xs: 80, sm: 120 }, 
                  p: { xs: 1, sm: 2 },
                  display: { xs: 'none', sm: 'table-cell' }
                }}>
                  <Typography variant="body2" fontWeight="bold">
                    Created
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 60, sm: 80 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(isLoadingData || isLoadingArchived) ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 4} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : displayTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 4} align="center" sx={{ py: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      {selectedTab === AVAILABLE_TASKS_TAB 
                        ? facilityFilterApplies
                          ? 'No unassigned tasks at the selected facilities'
                          : 'No unassigned tasks found' 
                        : tasksFilter === FILTER_ACTIVE 
                          ? 'No active tasks found' 
                          : 'No archived tasks found'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groupTasksByFacility(displayTasks).map(group => (
                  <Fragment key={group.key}>
                  {group.label && (
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 3 : 4}
                        sx={{ py: 1, px: { xs: 1, sm: 2 }, backgroundColor: 'grey.100' }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold">
                          {group.label} ({group.tasks.length})
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {group.tasks.map(task => (
                  <TableRow 
                    key={task.uuid} 
                    hover 
                    onClick={() => !isPrioritizeMode && handleViewTask(task)}
                    draggable={isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB} // Only draggable in prioritize mode in unassigned tasks tab
                    onDragStart={(e) => isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB && handleDragStart(e, task)}
                    onDragOver={(e) => isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB && handleDragOver(e, task)}
                    onDragLeave={() => isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB && handleDragLeave()}
                    onDrop={(e) => isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB && handleDrop(e, task)}
                    sx={{
                      cursor: isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB ? 'move' : 'pointer',
                      opacity: (shouldGrayOutTask(task) || task.is_paused) ? 0.5 : (dragOverTask?.uuid === task.uuid ? 0.5 : 1),
                      backgroundColor: dragOverTask?.uuid === task.uuid ? 'action.hover' : (task.is_paused ? 'grey.200' : (shouldGrayOutTask(task) ? 'grey.100' : (isPrioritizeMode && selectedTab === AVAILABLE_TASKS_TAB ? 'action.selected' : 'inherit'))),
                      borderTop: dragOverTask?.uuid === task.uuid ? '2px solid' : 'none',
                      borderColor: 'primary.main'
                    }}
                  >
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Chip
                        label={task.task_type?.type || 'Unknown'}
                        size="small"
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.8rem' },
                          ...(task.task_type?.type === 'SAFETY_CHECK'
                            ? { backgroundColor: '#0d9488', color: '#fff' }
                            : {})
                        }}
                        {...(task.task_type?.type !== 'SAFETY_CHECK' && { color: 'info' })}
                      />
                      {task.task_type?.type === 'MOVE_TRAILER' && task.from_object && (
                        <Box sx={{ mt: 0.5 }}>
                          {/* Facilities: source → destination on a cross-facility move, else the facility */}
                          {isMultiFacility && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                                fontWeight: 600,
                                lineHeight: 1.4
                              }}
                            >
                              {task.cross_facility
                                ? `${campusFacilityName(task.source_facility_id) || task.source_facility_id} → ${facilityShortLabel(task.facility) || task.facility_id}`
                                : facilityShortLabel(task.facility) || task.facility_id}
                            </Typography>
                          )}
                          {/* Zones: source zone → destination zone (zone-only locations show the zone itself) */}
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              color: 'primary.dark',
                              fontSize: { xs: '0.9rem', sm: '1.1rem' },
                              fontWeight: 600,
                              lineHeight: 1.4
                            }}
                          >
                            {getLocationZone(task.from_object) || '—'} → {task.to_object ? getLocationZone(task.to_object) || '—' : 'TBD'}
                          </Typography>
                          {/* Spots/docks: only when at least one end has one */}
                          {(getLocationSpot(task.from_object) || getLocationSpot(task.to_object)) && (
                            <Typography variant="caption" display="block" sx={{ 
                              color: 'text.primary',
                              fontSize: { xs: '0.9rem', sm: '1.1rem' },
                              fontWeight: 600,
                              lineHeight: 1.4
                            }}>
                              {getLocationSpot(task.from_object) || '—'} → {getLocationSpot(task.to_object) || '—'}
                            </Typography>
                          )}
                          {task.trailer && (
                            <Typography variant="caption" display="block" sx={{ 
                              color: 'text.primary',
                              fontSize: { xs: '0.9rem', sm: '1.1rem' },
                              fontWeight: 600,
                              lineHeight: 1.4
                            }}>
                              {task.trailer.trailer_number} | {task.trailer.carrier_name || 'N/A'}
                            </Typography>
                          )}
                          {/* Show grouped tasks count for unassigned tasks */}
                          {selectedTab === AVAILABLE_TASKS_TAB && task.data?.task_group_uuid && (() => {
                            const relatedTasksCount = tasks.filter(t => 
                              t.data?.task_group_uuid === task.data?.task_group_uuid
                            ).length;
                            return null;
                          })()}
                        </Box>
                      )}
                      {task.task_type?.type === 'SAFETY_CHECK' && task.trailer && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" display="block" sx={{ 
                            color: 'text.primary',
                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                            fontWeight: 600,
                            lineHeight: 1.4
                          }}>
                            {task.related_movement_task && task.related_movement_task.from_object ? (
                              <>Safety Check: {getLocationName(task.related_movement_task.from_object)} → {task.related_movement_task.to_object ? getLocationName(task.related_movement_task.to_object) : 'TBD'}</>
                            ) : (
                              <>{task.data?.description || (task.data?.dock_uuid ? 'Dock Safety Check' : task.data?.yard_spot_uuid ? 'Yard Safety Check' : 'Safety Check')}</>
                            )}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ 
                            color: 'text.primary',
                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                            fontWeight: 600,
                            lineHeight: 1.4
                          }}>
                            {task.trailer.trailer_number} | {task.trailer.carrier_name || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                      {task.task_type?.type === 'WORKFLOW' && task.data?.workflow_type && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" display="block" sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.6rem', sm: '0.75rem' }
                          }}>
                            {formatWorkflowType(task.data.workflow_type)}
                          </Typography>
                          {isWorkflowWithChecklist(task) && (
                            <Typography variant="caption" display="block" sx={{ 
                              color: canCompleteWorkflowTask(task) ? 'success.main' : 'warning.main',
                              fontSize: { xs: '0.6rem', sm: '0.75rem' },
                              fontWeight: 'bold'
                            }}>
                              Checklist: {checklistCompletionState[task.uuid] ? 
                                `${task.data.checklist.length}/${task.data.checklist.length}` : 
                                `0/${task.data.checklist.length}`} completed
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Chip
                        label={task.is_paused ? 'PAUSED' : (task.task_status?.status || 'Unknown')}
                        color={task.is_paused ? 'default' : getStatusColor(task.task_status?.status)}
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      p: { xs: 1, sm: 2 },
                      display: { xs: 'none', sm: 'table-cell' }
                    }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                        {formatDateTime(task.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} sx={{ p: { xs: 1, sm: 2 } }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Main Action Button */}
                        {selectedTab === AVAILABLE_TASKS_TAB ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip
                              title={task.is_paused ? 'Resume task' : 'Pause task'}
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleTaskPause(task.uuid)}
                                  disabled={processingTasks.has(task.uuid)}
                                  sx={{
                                    backgroundColor: 'black',
                                    color: 'white',
                                    width: { xs: 24, sm: 28 },
                                    height: { xs: 24, sm: 28 },
                                    '&:hover': { backgroundColor: 'grey.800' }
                                  }}
                                >
                                  {task.is_paused ?
                                    <PlayArrow sx={{ fontSize: { xs: 14, sm: 16 } }} /> :
                                    <Pause sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                  }
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                task.is_paused ? "Task is paused" :
                                userHasActiveTasks ? "Complete your active tasks before taking new ones" : ""
                              }
                              arrow
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={
                                    processingTasks.has(task.uuid) ?
                                    <CircularProgress size={12} color="inherit" /> :
                                    <AssignmentInd sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                  }
                                  onClick={() => handleAssignTask(task.uuid)}
                                  disabled={processingTasks.has(task.uuid) || userHasActiveTasks || task.is_paused}
                                  sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                    minWidth: { xs: 60, sm: 'auto' },
                                    px: { xs: 1, sm: 2 }
                                  }}
                                >
                                                            {processingTasks.has(task.uuid) ? 'Assigning...' : (() => {
                                                                const relatedTasksCount = task.data?.task_group_uuid ?
                                    groupedTasks.filter(t => t.data?.task_group_uuid === task.data?.task_group_uuid).length : 1;
                                    return relatedTasksCount > 1 ? `Take ${relatedTasksCount}` : 'Take';
                              })()}
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {/* Check if task is archived (completed or cancelled) */}
                            {tasksFilter === FILTER_ARCHIVED ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                                Archived
                              </Typography>
                            ) : (
                            <>
                              <Tooltip 
                                title={
                                  shouldGrayOutTask(task) ? "Complete your MOVE_TRAILER task first" :
                                  isWorkflowWithChecklist(task) && !canCompleteWorkflowTask(task) 
                                    ? "Complete all checklist items before finishing this task" 
                                    : ""
                                }
                                arrow
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={
                                      processingTasks.has(task.uuid) ? 
                                      <CircularProgress size={12} color="inherit" /> : 
                                      <CheckCircle sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                    }
                                    onClick={() => {
                                      if (task.task_type?.type === 'MOVE_TRAILER' && task.requires_destination_selection) {
                                        handleChangeDestination(task.uuid);
                                      } else if (task.task_type?.type === 'MOVE_TRAILER') {
                                        handleCompleteMovementTask(task.uuid);
                                      } else {
                                        handleCompleteTask(task.uuid);
                                      }
                                    }}
                                    disabled={
                                      processingTasks.has(task.uuid) || 
                                      shouldGrayOutTask(task) ||
                                      (isWorkflowWithChecklist(task) && !canCompleteWorkflowTask(task))
                                    }
                                    sx={{ 
                                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                      minWidth: { xs: 50, sm: 'auto' },
                                      px: { xs: 1, sm: 2 }
                                    }}
                                  >
                                    {processingTasks.has(task.uuid) ? 'Completing...' : 'Complete'}
                                  </Button>
                                </span>
                              </Tooltip>
                              {task.task_type?.type === 'MOVE_TRAILER' && (
                                <Tooltip title="Change the destination before completing" arrow>
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="info"
                                      startIcon={<SwapHoriz sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                                      onClick={() => handleChangeDestination(task.uuid)}
                                      disabled={processingTasks.has(task.uuid) || shouldGrayOutTask(task)}
                                      sx={{
                                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                        minWidth: { xs: 50, sm: 'auto' },
                                        px: { xs: 1, sm: 2 }
                                      }}
                                    >
                                      Change Destination
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                              {canCancelTasks(user) && (
                                <Tooltip
                                  title={getCancelTooltipMessage(task)}
                                  arrow
                                >
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      startIcon={
                                        processingTasks.has(task.uuid) ? 
                                        <CircularProgress size={12} color="inherit" /> : 
                                        <Cancel sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                      }
                                      onClick={() => { if (window.confirm('Are you sure you want to cancel this task?')) { handleCancelTask(task.uuid) } }}
                                      disabled={processingTasks.has(task.uuid) || shouldGrayOutTask(task) || !canCancelTask(task)}
                                      sx={{ 
                                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                        minWidth: { xs: 50, sm: 'auto' },
                                        px: { xs: 1, sm: 2 }
                                      }}
                                    >
                                      {processingTasks.has(task.uuid) ? 'Cancelling...' : 'Cancel'}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                              <Tooltip 
                                title={shouldGrayOutTask(task) ? "Complete your MOVE_TRAILER task first" : (() => {
                                                                  const relatedTasksCount = task.data?.task_group_uuid ?
                                groupedTasks.filter(t => t.data?.task_group_uuid === task.data?.task_group_uuid).length : 1;
                                return relatedTasksCount > 1 ? `This will unassign ${relatedTasksCount} related tasks` : "";
                                })()}
                                arrow
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={
                                      processingTasks.has(task.uuid) ? 
                                      <CircularProgress size={12} color="inherit" /> : 
                                      <AssignmentReturn sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                    }
                                    onClick={() => handleUnassignTask(task.uuid)}
                                    disabled={processingTasks.has(task.uuid) || shouldGrayOutTask(task)}
                                    sx={{ 
                                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                      minWidth: { xs: 50, sm: 'auto' },
                                      px: { xs: 1, sm: 2 }
                                    }}
                                  >
                                    {processingTasks.has(task.uuid) ? 'Unassigning...' : (() => {
                                                                        const relatedTasksCount = task.data?.task_group_uuid ?
                                  groupedTasks.filter(t => t.data?.task_group_uuid === task.data?.task_group_uuid).length : 1;
                                  return relatedTasksCount > 1 ? `Unassign All` : 'Unassign';
                                    })()}
                                  </Button>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  ))}
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Task Detail Modal */}
      <Dialog
        open={isTaskDetailModalOpen}
        onClose={handleCloseTaskDetail}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Task Details</Typography>
            <IconButton onClick={handleCloseTaskDetail}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box sx={{ pt: { xs: 1, sm: 2 } }}>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Basic Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Type</Typography>
                        <Chip
                          label={selectedTask.task_type?.type || 'Unknown'}
                          size="small"
                          sx={selectedTask.task_type?.type === 'SAFETY_CHECK' ? {
                            backgroundColor: '#0d9488',
                            color: '#fff'
                          } : undefined}
                          {...(selectedTask.task_type?.type !== 'SAFETY_CHECK' && { color: 'info' })}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Chip
                          label={selectedTask.is_paused ? 'PAUSED' : (selectedTask.task_status?.status || 'Unknown')}
                          color={selectedTask.is_paused ? 'default' : getStatusColor(selectedTask.task_status?.status)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Created</Typography>
                        <Typography variant="body1">{formatDateTime(selectedTask.created_at)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Trailer Notes Section - Only show for My Tasks tab and if there's a trailer */}
                  {selectedTab === MY_TASKS_TAB && selectedTask.trailer && selectedTask.data?.trailer_uuid && (
                    <Card>
                      <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: 0 }}>
                            Trailer Notes
                          </Typography>
                          {!isEditingTrailerNotes && (
                            <IconButton 
                              size="small" 
                              onClick={() => setIsEditingTrailerNotes(true)}
                              sx={{ ml: 1 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        
                                                 {isEditingTrailerNotes ? (
                           <Box>
                             {/* Set Note Dropdown */}
                             <Box sx={{ mb: 2 }}>
                               <FormControl size="small" sx={{ minWidth: 200 }}>
                                 <InputLabel>Set note</InputLabel>
                                 <Select
                                   value=""
                                   onChange={(e) => setTrailerNotesValue(e.target.value)}
                                   label="Set note"
                                   disabled={savingTrailerNotes}
                                 >
                                   <MenuItem value="" disabled>
                                     <Typography variant="body2" color="text.secondary">
                                       Select a template...
                                     </Typography>
                                   </MenuItem>
                                   
                                   {/* Materials Group */}
                                   <MenuItem disabled sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                     Materials
                                   </MenuItem>
                                   <MenuItem value="PET bottles - incoming raw materials">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       PET bottles - incoming raw materials
                                     </Typography>
                                   </MenuItem>
                                   <MenuItem value="Caps and closures - incoming packaging materials">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Caps and closures - incoming packaging materials
                                     </Typography>
                                   </MenuItem>
                                   <MenuItem value="Labels - incoming packaging materials">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Labels - incoming packaging materials
                                     </Typography>
                                   </MenuItem>
                                   <MenuItem value="Water treatment chemicals - incoming materials">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Water treatment chemicals - incoming materials
                                     </Typography>
                                   </MenuItem>
                                   <MenuItem value="Cardboard cases - incoming packaging materials">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Cardboard cases - incoming packaging materials
                                     </Typography>
                                   </MenuItem>
                                   <MenuItem value="Shrink wrap - incoming packaging materials">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Shrink wrap - incoming packaging materials
                                     </Typography>
                                   </MenuItem>
                                   <MenuItem value="Pallets - incoming logistics materials">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Pallets - incoming logistics materials
                                     </Typography>
                                   </MenuItem>
                                   
                                   {/* Product Group */}
                                   <MenuItem disabled sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1 }}>
                                     Product
                                   </MenuItem>
                                   <MenuItem value="Product1 - incoming finished goods">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Product1 - incoming finished goods
                                     </Typography>
                                   </MenuItem>
                                   <MenuItem value="Product2 - incoming finished goods">
                                     <Typography variant="body2" sx={{ pl: 2 }}>
                                       Product2 - incoming finished goods
                                     </Typography>
                                   </MenuItem>
                                 </Select>
                               </FormControl>
                             </Box>
                             
                             <textarea
                               value={trailerNotesValue}
                               onChange={(e) => setTrailerNotesValue(e.target.value)}
                               placeholder="Enter notes about this trailer..."
                               style={{
                                 width: '100%',
                                 padding: '8px',
                                 border: '1px solid #ccc',
                                 borderRadius: '4px',
                                 minHeight: '80px',
                                 marginBottom: '8px',
                                 fontFamily: 'inherit',
                                 fontSize: '14px',
                                 resize: 'vertical'
                               }}
                               disabled={savingTrailerNotes}
                             />
                             <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                               <Button
                                 size="small"
                                 color="inherit"
                                 onClick={() => {
                                   setTrailerNotesValue(selectedTask.trailer?.lineage_notes || '');
                                   setIsEditingTrailerNotes(false);
                                 }}
                                 disabled={savingTrailerNotes}
                                 sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                               >
                                 Cancel
                               </Button>
                               <Button
                                 size="small"
                                 color="success"
                                 variant="contained"
                                 startIcon={savingTrailerNotes ? <CircularProgress size={12} /> : undefined}
                                 onClick={handleSaveTrailerNotes}
                                 disabled={savingTrailerNotes}
                                 sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                               >
                                 {savingTrailerNotes ? 'Saving...' : 'Save Notes'}
                               </Button>
                             </Box>
                           </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              p: 2, 
                              border: 1, 
                              borderColor: 'divider', 
                              borderRadius: 1, 
                              backgroundColor: selectedTask.trailer.lineage_notes ? 'background.paper' : 'grey.50'
                            }}
                          >
                            {selectedTask.trailer.lineage_notes ? (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {selectedTask.trailer.lineage_notes}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No trailer notes added
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Task Details
                      </Typography>
                      
                      {selectedTask.task_type?.type === 'MOVE_TRAILER' && (
                        <>
                          {selectedTask.from_object && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">From Location</Typography>
                              <Typography variant="body1">{getLocationName(selectedTask.from_object)}</Typography>
                            </Box>
                          )}
                          {selectedTask.to_object && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">To Location</Typography>
                              <Typography variant="body1">{getLocationName(selectedTask.to_object)}</Typography>
                            </Box>
                          )}
                          {selectedTask.trailer && (
                            <>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">Trailer</Typography>
                                <Typography variant="body1">{selectedTask.trailer.trailer_number}</Typography>
                              </Box>
                              {selectedTask.trailer.carrier_name && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary">Carrier</Typography>
                                  <Typography variant="body1">{selectedTask.trailer.carrier_name}</Typography>
                                </Box>
                              )}
                            </>
                          )}


                        </>
                      )}

                      {selectedTask.task_type?.type === 'WORKFLOW' && selectedTask.data && (
                        <>
                          {selectedTask.data.description && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Description</Typography>
                              <Typography variant="body1">{selectedTask.data.description}</Typography>
                            </Box>
                          )}
                          {selectedTask.data.workflow_type && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">Workflow Type</Typography>
                              <Typography variant="body1">{formatWorkflowType(selectedTask.data.workflow_type)}</Typography>
                            </Box>
                          )}
                          {selectedTask.data.checklist && Array.isArray(selectedTask.data.checklist) && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Checklist ({selectedTask.data.checklist.length} items)
                                </Typography>
                                <Chip 
                                  label={`${checklistCompletionState[selectedTask.uuid] ? 
                                    selectedTask.data.checklist.length : 0}/${selectedTask.data.checklist.length} completed`}
                                  size="small"
                                  color={canCompleteWorkflowTask(selectedTask) ? "success" : "warning"}
                                  variant="outlined"
                                />
                              </Box>
                              
                              {/* Single checkbox for completing all checklist items */}
                              <Box sx={{ mb: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={checklistCompletionState[selectedTask.uuid] || false}
                                      onChange={(e) => handleChecklistChange(selectedTask.uuid, e.target.checked)}
                                      size="small"
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography 
                                      variant="body2"
                                      sx={{ 
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      Mark all checklist items as complete
                                    </Typography>
                                  }
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                    ml: 0
                                  }}
                                />
                              </Box>
                              
                              {/* Display checklist items for reference */}
                              <Box sx={{ pl: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                                  Checklist Items:
                                </Typography>
                                {selectedTask.data.checklist.map((item: string, index: number) => (
                                  <Typography 
                                    key={index}
                                    variant="body2"
                                    sx={{ 
                                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                      mb: 0.5,
                                      pl: 2,
                                      color: 'text.secondary'
                                    }}
                                  >
                                    • {item}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}

                      {/* Show related tasks information for tasks with trailer_uuid */}
                      {selectedTask.data?.task_group_uuid && selectedTab === AVAILABLE_TASKS_TAB && (() => {
                        const relatedTasks = tasks.filter(t => 
                          t.data?.task_group_uuid === selectedTask.data?.task_group_uuid
                        );
                        return relatedTasks.length > 1 ? (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">Related Tasks</Typography>
                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                              {relatedTasks.length} total tasks will be assigned together:
                            </Typography>
                            <Box sx={{ pl: 2, mt: 1 }}>
                              {relatedTasks.map((relatedTask, index) => (
                                <Typography 
                                  key={relatedTask.uuid}
                                  variant="body2" 
                                  sx={{ 
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    color: relatedTask.uuid === selectedTask.uuid ? 'primary.main' : 'text.secondary',
                                    fontWeight: relatedTask.uuid === selectedTask.uuid ? 'bold' : 'normal'
                                  }}
                                >
                                  {index + 1}. {relatedTask.task_type?.type || 'Unknown'} 
                                  {relatedTask.uuid === selectedTask.uuid && ' (current)'}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        ) : null;
                      })()}

                      {selectedTask.task_template && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">Template</Typography>
                          <Typography variant="body1">{selectedTask.task_template.name}</Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTask && selectedTab === AVAILABLE_TASKS_TAB && (
            <>
              <Tooltip
                title={selectedTask.is_paused ? 'Resume task' : 'Pause task'}
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    color={selectedTask.is_paused ? 'success' : 'warning'}
                    startIcon={
                      processingTasks.has(selectedTask.uuid) ?
                      <CircularProgress size={16} color="inherit" /> :
                      selectedTask.is_paused ? <PlayArrow /> : <Pause />
                    }
                    onClick={() => handleToggleTaskPause(selectedTask.uuid)}
                    disabled={processingTasks.has(selectedTask.uuid)}
                  >
                    {selectedTask.is_paused ? 'Resume' : 'Pause'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  selectedTask.is_paused ? "Task is paused" :
                  userHasActiveTasks ? "Complete your active tasks before taking new ones" : ""
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={processingTasks.has(selectedTask.uuid) ? <CircularProgress size={16} color="inherit" /> : <AssignmentInd />}
                    onClick={() => handleAssignTask(selectedTask.uuid)}
                    disabled={processingTasks.has(selectedTask.uuid) || userHasActiveTasks || selectedTask.is_paused}
                  >
                    {processingTasks.has(selectedTask.uuid) ? 'Assigning...' : (() => {
                                                          const relatedTasksCount = selectedTask.data?.task_group_uuid ?
                                      groupedTasks.filter(t => t.data?.task_group_uuid === selectedTask.data?.task_group_uuid).length : 1;
                                      return relatedTasksCount > 1 ? `Take ${relatedTasksCount} Tasks` : 'Take Task';
                    })()}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

        </DialogActions>
      </Dialog>
      
      {/* Movement Task Completion Modal */}
      <MovementTaskCompletionModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setTaskToComplete(null);
        }}
        task={taskToComplete}
        onConfirm={handleCompleteMovementTaskWithDestination}
        requireSpecificDestination={!!taskToComplete?.requires_destination_selection}
        facilities={modalFacilities}
        facilityZones={modalFacilityZones}
        yardSpots={modalYardSpots}
        zones={modalZones || []}
        warehouseZones={modalWarehouseData?.warehouseZones || []}
        docks={modalWarehouseData?.warehouseZones?.flatMap((zone: any) => 
          (zone.docks || []).map((dock: any) => ({
          uuid: dock.dockUuid,
          id: dock.dockUuid,
          name: dock.dockName,
          display_name: dock.dockName,
          door_name: dock.dockName,
          trailer_name: dock.trailer_name,
          warehouse_zone_uuid: zone.zoneUuid,
          warehouse_zone_name: zone.zoneName,
          configuration: dock.configuration
        }))
        ) || []}
        lockedSpots={modalLockedSpots}
      />
      </div>
    </Box>
  );
};

export default TaskApp; 